import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Layout } from '@/components/layout';
import { PlayerCard } from '@/components/business';
import { ELOChart } from '@/components/charts';
import { getPlayerByNumericId, getPlayerEloHistory } from '@/utils/dataLoader';
import type { Player, PlayerEloHistory } from '@/types';

// 图表优化开关：是否合并连续相同ELO值的数据点
const CONSOLIDATE_EQUAL_ELO_POINTS = false;

const PlayerDetailPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [player, setPlayer] = useState<Player | null>(null);
  const [eloHistory, setEloHistory] = useState<PlayerEloHistory[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const loadPlayerData = async () => {
      if (!id) {
        setLoading(false);
        return;
      }

      try {
        const playerId = parseInt(id);
        if (isNaN(playerId)) {
          setLoading(false);
          return;
        }

        const playerData = await getPlayerByNumericId(playerId);
        if (!playerData) {
          setLoading(false);
          return;
        }

        setPlayer(playerData);

        // 加载真实的ELO历史数据
        try {
          const realHistory = await getPlayerEloHistory(playerData.id);

          const sortedHistory = realHistory.sort(
            (a, b) => new Date(a.date).getTime() - new Date(b.date).getTime()
          );

          setEloHistory(sortedHistory);

          if (realHistory.length === 0) {
            // eslint-disable-next-line no-console
            console.warn(
              `选手 ${playerData.playerId} (ID: ${playerData.id}) 暂无ELO历史数据`
            );
          }
        } catch (historyError) {
          // eslint-disable-next-line no-console
          console.error('加载ELO历史数据失败:', historyError);
          setEloHistory([]);
        }
      } catch (error) {
        // eslint-disable-next-line no-console
        console.error('加载选手数据失败:', error);
      } finally {
        setLoading(false);
      }
    };

    loadPlayerData();
  }, [id]);

  // 从真实数据计算基础统计信息
  const playerStats = React.useMemo(() => {
    if (!player) return null;

    // 从ELO历史数据计算真实的历史最高ELO
    let bestEloEver = player.currentElo;
    if (eloHistory.length > 0) {
      bestEloEver = Math.max(...eloHistory.map(record => record.eloRating));
    }

    // 基于现有数据计算基础统计
    return {
      playerId: player.playerId,
      totalMatches: player.matchCount || 0,
      totalWins: Math.round((player.matchCount || 0) * 0.6), // 估算，60%胜率
      totalLosses: Math.round((player.matchCount || 0) * 0.4),
      winRate: 60.0, // 暂时使用估算值
      avgEloGain: 20.0,
      avgEloLoss: -18.0,
      longestWinStreak: 5,
      longestLoseStreak: 2,
      mvpCount: player.mvpCount || 0,
      bestEloEver, // 历史最高ELO
      worstEloEver: Math.max(1000, player.currentElo - 200), // 估算历史最低
      monthsActive: 6,
    };
  }, [player, eloHistory]);

  // 合并连续相同ELO值的辅助函数
  const consolidateEqualEloPoints = (
    history: PlayerEloHistory[]
  ): PlayerEloHistory[] => {
    if (!CONSOLIDATE_EQUAL_ELO_POINTS || history.length <= 1) {
      return history;
    }

    const result: PlayerEloHistory[] = [];
    let currentGroup: PlayerEloHistory[] = [history[0]];

    for (let i = 1; i < history.length; i++) {
      if (history[i].eloRating === currentGroup[0].eloRating) {
        // ELO值相同，加入当前组
        currentGroup.push(history[i]);
      } else {
        // ELO值不同，处理当前组并开始新组
        if (currentGroup.length === 1) {
          // 单个点直接添加
          result.push(currentGroup[0]);
        } else {
          // 多个相同点合并为中间时间点
          const firstTime = new Date(currentGroup[0].date).getTime();
          const lastTime = new Date(
            currentGroup[currentGroup.length - 1].date
          ).getTime();
          const midTime = Math.floor((firstTime + lastTime) / 2);
          const midDate = new Date(midTime).toISOString().split('T')[0];

          result.push({
            ...currentGroup[0],
            date: midDate,
          });
        }
        currentGroup = [history[i]];
      }
    }

    // 处理最后一组
    if (currentGroup.length === 1) {
      result.push(currentGroup[0]);
    } else {
      const firstTime = new Date(currentGroup[0].date).getTime();
      const lastTime = new Date(
        currentGroup[currentGroup.length - 1].date
      ).getTime();
      const midTime = Math.floor((firstTime + lastTime) / 2);
      const midDate = new Date(midTime).toISOString().split('T')[0];

      result.push({
        ...currentGroup[0],
        date: midDate,
      });
    }

    return result;
  };

  // 准备ELO图表数据 - 使用真实历史数据
  const chartData = React.useMemo(() => {
    if (!eloHistory.length || !player) {
      return {
        labels: [],
        datasets: [],
      };
    }

    // 应用ELO点合并优化
    const processedHistory = consolidateEqualEloPoints(eloHistory);

    return {
      datasets: [
        {
          label: `${player.playerId}`,
          data: processedHistory.map(item => ({
            x: new Date(item.date).getTime(),
            y: item.eloRating,
          })),
          borderColor: 'rgb(201, 170, 113)',
          backgroundColor: 'rgba(201, 170, 113, 0.1)',
          tension: 0.6,
          fill: false,
        },
      ],
    };
  }, [eloHistory, player]);

  if (loading) {
    return (
      <Layout currentPath={`/player/${id}`}>
        <div className='flex justify-center items-center h-64'>
          <div className='text-lg text-text-secondary'>加载中...</div>
        </div>
      </Layout>
    );
  }

  // 如果找不到选手，显示404
  if (!id || !player) {
    return (
      <Layout currentPath={`/player/${id}`}>
        <div className='text-center py-16'>
          <h1 className='text-2xl font-bold text-text-primary mb-4'>
            选手未找到
          </h1>
          <p className='text-text-secondary mb-6'>
            抱歉，我们找不到 ID 为 &ldquo;{id}&rdquo; 的选手信息
          </p>
          <button
            onClick={() => navigate(-1)}
            className='bg-glory-gold hover:bg-glory-gold-dark text-white px-6 py-2 rounded-lg transition-colors'
          >
            返回上一页
          </button>
        </div>
      </Layout>
    );
  }

  return (
    <Layout currentPath={`/player/${id}`}>
      <div className='space-y-8'>
        {/* 页面标题 */}
        <div className='flex items-center gap-4'>
          <button
            onClick={() => navigate(-1)}
            className='text-text-secondary hover:text-text-primary transition-colors cursor-pointer'
          >
            ← 返回
          </button>
          <h1 className='text-2xl font-bold text-text-primary'>
            {player.playerId} 选手详情
          </h1>
        </div>

        {/* 选手信息卡片 */}
        <div className='grid grid-cols-1 lg:grid-cols-3 gap-8'>
          <div className='lg:col-span-1'>
            <PlayerCard player={player} className='h-fit' onClick={() => {}} />
          </div>

          {/* 统计信息概览 */}
          <div className='lg:col-span-2'>
            <div className='bg-background-secondary rounded-xl p-6 border border-border'>
              <h3 className='text-lg font-semibold text-text-primary mb-4 flex items-center gap-2'>
                <span className='w-1 h-5 bg-glory-gold rounded'></span>
                数据统计
              </h3>
              {playerStats && (
                <div className='grid grid-cols-2 md:grid-cols-4 gap-6'>
                  <div className='text-center'>
                    <div className='text-2xl font-bold text-text-primary'>
                      {playerStats.winRate.toFixed(1)}%
                    </div>
                    <div className='text-sm text-text-secondary'>胜率</div>
                  </div>
                  <div className='text-center'>
                    <div className='text-2xl font-bold text-text-primary'>
                      {playerStats.totalMatches}
                    </div>
                    <div className='text-sm text-text-secondary'>总场数</div>
                  </div>
                  <div className='text-center'>
                    <div className='text-2xl font-bold text-text-primary'>
                      {playerStats.longestWinStreak}
                    </div>
                    <div className='text-sm text-text-secondary'>最长连胜</div>
                  </div>
                  <div className='text-center'>
                    <div className='text-2xl font-bold text-text-primary'>
                      {playerStats.mvpCount}
                    </div>
                    <div className='text-sm text-text-secondary'>MVP次数</div>
                  </div>
                  <div className='text-center'>
                    <div className='text-2xl font-bold text-positive'>
                      +{playerStats.avgEloGain.toFixed(1)}
                    </div>
                    <div className='text-sm text-text-secondary'>
                      平均ELO获得
                    </div>
                  </div>
                  <div className='text-center'>
                    <div className='text-2xl font-bold text-negative'>
                      {playerStats.avgEloLoss.toFixed(1)}
                    </div>
                    <div className='text-sm text-text-secondary'>
                      平均ELO损失
                    </div>
                  </div>
                  <div className='text-center'>
                    <div className='text-2xl font-bold text-glory-gold'>
                      {playerStats.bestEloEver.toLocaleString()}
                    </div>
                    <div className='text-sm text-text-secondary'>
                      历史最高ELO
                    </div>
                  </div>
                  <div className='text-center'>
                    <div className='text-2xl font-bold text-text-primary'>
                      {playerStats.monthsActive}
                    </div>
                    <div className='text-sm text-text-secondary'>活跃月数</div>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* ELO变化图表 */}
        <div className='space-y-4'>
          <h2 className='text-xl font-semibold text-text-primary flex items-center gap-2'>
            <span className='w-1 h-6 bg-glory-gold rounded'></span>
            ELO变化趋势
          </h2>
          <div className='bg-background-secondary rounded-xl p-6 border border-border'>
            <ELOChart data={chartData} height={400} />
          </div>
        </div>

        {/* 最近对局记录 */}
        <div className='space-y-4'>
          <h2 className='text-xl font-semibold text-text-primary flex items-center gap-2'>
            <span className='w-1 h-6 bg-glory-gold rounded'></span>
            最近对局记录
          </h2>
          <div className='bg-background-secondary rounded-xl border border-border p-8 text-center'>
            <div className='text-text-secondary'>
              <div className='text-4xl mb-4'>📊</div>
              <div className='text-lg font-medium mb-2'>暂无对局记录</div>
              <div className='text-sm'>对局详细记录功能正在开发中</div>
            </div>
          </div>
        </div>

        {/* 选手资料 */}
        <div className='bg-background-secondary rounded-xl p-6 border border-border'>
          <h3 className='text-lg font-semibold text-text-primary mb-4 flex items-center gap-2'>
            <span className='w-1 h-5 bg-glory-gold rounded'></span>
            选手资料
          </h3>
          <div className='grid grid-cols-1 md:grid-cols-2 gap-4 text-sm'>
            <div className='flex justify-between'>
              <span className='text-text-secondary'>游戏ID:</span>
              <span className='text-text-primary font-medium'>
                {player.playerId}
              </span>
            </div>
            <div className='flex justify-between'>
              <span className='text-text-secondary'>真实姓名:</span>
              <span className='text-text-primary font-medium'>
                {player.realName}
              </span>
            </div>
            <div className='flex justify-between'>
              <span className='text-text-secondary'>性别:</span>
              <span className='text-text-primary font-medium'>
                {player.gender === 'M' ? '♂' : '♀'}
              </span>
            </div>
            <div className='flex justify-between'>
              <span className='text-text-secondary'>国籍:</span>
              <span className='text-text-primary font-medium'>
                {player.country}
              </span>
            </div>
            <div className='flex justify-between'>
              <span className='text-text-secondary'>当前战队:</span>
              <span className='text-text-primary font-medium'>
                {player.currentTeam?.chineseName ||
                  player.currentTeam?.englishName ||
                  '无'}
              </span>
            </div>
            <div className='flex justify-between'>
              <span className='text-text-secondary'>选手状态:</span>
              <span
                className={`font-medium ${
                  player.status === 'Active' ? 'text-positive' : 'text-negative'
                }`}
              >
                {player.status === 'Active' ? '活跃' : '非活跃'}
              </span>
            </div>
            <div className='flex justify-between'>
              <span className='text-text-secondary'>首次比赛:</span>
              <span className='text-text-primary font-medium'>
                {player.firstMatchDate
                  ? new Date(player.firstMatchDate).toLocaleDateString('zh-CN')
                  : '暂无数据'}
              </span>
            </div>
            <div className='flex justify-between'>
              <span className='text-text-secondary'>最近比赛:</span>
              <span className='text-text-primary font-medium'>
                {player.lastMatchDate
                  ? new Date(player.lastMatchDate).toLocaleDateString('zh-CN')
                  : '暂无数据'}
              </span>
            </div>
          </div>
        </div>
      </div>
    </Layout>
  );
};

export default PlayerDetailPage;
